import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "@contracts/generated/editor-service";

import { isSigningOut } from "@/auth/signOutState";
import type { AuthAdapter } from "@/auth/types";
import type { RuntimeConfig } from "@/config/schema";

/**
 * Typed Editor Service client.
 *
 * `paths` is generated from contracts/editor-service.openapi.yaml. No request
 * or response type in this application is written by hand, which is what issue
 * #4 means by "no request/response schema is hand-copied from Python".
 */
export type ApiClient = ReturnType<typeof createClient<paths>>;

/** Error surfaced to the UI. Carries a code, never clinical content. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  /**
   * `ErrorDetail.details`, verbatim and untyped at this layer.
   *
   * The contract attaches it to the `github_*` codes and documents the reason:
   * "the error channel is the push channel — no polling". A publication refused
   * with `adoption_preflight_refused` therefore carries the refused pins, so one
   * failed action tells the caller what to resend without a second request.
   *
   * Left as `unknown` deliberately. This module is the transport for every
   * endpoint and must not grow a domain-shaped field; each domain module
   * narrows it (`archiveRefusalFromError`), which keeps the widening the
   * contract already anticipates — "future payload kinds widen this union" —
   * a change in one place rather than here.
   */
  readonly details: unknown;

  /**
   * The `error` object exactly as it arrived, for the flat fields the contract
   * attaches to specific codes — `currentDraftVersion`, `currentAssetVersion`,
   * `currentPreviewTargetRevision`, `archiveRefusalReason`.
   *
   * Deliberately an untyped bag, for the same reason `details` is untyped: this
   * module is the transport for every endpoint and must not grow a field shaped
   * like any one domain. Before this existed the flat fields were dropped here,
   * so a recovery hint the server had already computed could not reach the
   * component that needed it — the caller was left matching on `message` prose,
   * which is what those fields exist to replace. Domain modules narrow it
   * (`archiveRefusalFromError`), exactly as `adoptionRefusalFromError` narrows
   * `details`.
   */
  readonly envelope: Readonly<Record<string, unknown>>;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId?: string,
    details?: unknown,
    envelope?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
    this.envelope = envelope ?? {};
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * The endpoint exists in the contract but the Editor Service has not built it
   * yet. Issue #13 ships its schemas ahead of its handlers, so this is an
   * expected, and permanent-until-deployed, condition — not a transient fault.
   * Retrying it can never succeed, so the UI must not offer to.
   */
  get isNotImplemented(): boolean {
    return this.status === 501;
  }

  /**
   * Optimistic-concurrency conflict: the resource moved under the caller.
   *
   * Tested on **status alone**. `runValidation`'s 409 is declared in the
   * contract as a bare `description:` with no `content:`, so an `ErrorEnvelope`
   * — and therefore `code` — is not guaranteed on that response.
   * (`patchDraft`'s 409 in the same document does declare one; the omission is
   * isolated to `runValidation`, and #16 is asked to close it.) Reading
   * `error.code` here would make the stale-pin path depend on a body the
   * contract never promised.
   */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

function authMiddleware(adapter: AuthAdapter): Middleware {
  const refusal = () =>
    new ApiError(
      401,
      "signing_out",
      "Sign-out is in progress; the request was not sent.",
    );
  return {
    async onRequest({ request }) {
      // Ordered sign-out (#44): once sign-out begins — in this tab or any
      // other — nothing further may leave the application, mutations above
      // all. Refusing here, before the network, is what makes "sign-out
      // cannot race an in-flight redirect" true rather than probabilistic.
      if (isSigningOut()) {
        throw refusal();
      }
      const token = await adapter.getAccessToken();
      // Checked again, not redundantly: token acquisition yields, and the
      // gate can close — locally or by another tab's broadcast — while it is
      // pending. A single check before the await would let this request
      // continue to fetch, token attached, after sign-out began.
      if (isSigningOut()) {
        throw refusal();
      }
      if (token) {
        request.headers.set("authorization", `Bearer ${token}`);
      }
      return request;
    },
  };
}

export function createApiClient(
  config: RuntimeConfig,
  adapter: AuthAdapter,
  /**
   * The transport, injectable for the one case #300 leaves open.
   *
   * Component tests run against a REAL Editor Service now. A few of them assert
   * behaviour on a status a healthy service will not produce on demand — a
   * `500`, a transport failure, one of the `501`s #19 PR 3 retired — and the
   * clarified rule permits a single scripted response declared inside the one
   * test that needs it. This parameter is where that response is handed in.
   *
   * It exists so such a test never replaces the GLOBAL `fetch`, which would
   * apply to everything else in the same worker and is the first step back
   * towards a shared response catalogue. It follows the seam
   * `loadRuntimeConfig(fetchImpl)` has used for the same reason since #4.
   *
   * The default is unchanged, and still resolves through `globalThis` per
   * request rather than capturing it at construction: anything that installs
   * itself over `fetch` after this client exists would otherwise be bypassed,
   * and the resulting failure looks like a network outage rather than a wiring
   * mistake.
   */
  transport: typeof globalThis.fetch = (request) => globalThis.fetch(request),
): ApiClient {
  const client = createClient<paths>({
    baseUrl: config.editorServiceBaseUrl,
    credentials: "omit",
    headers: { accept: "application/json" },
    fetch: transport,
  });
  client.use(authMiddleware(adapter));
  return client;
}

type ErrorEnvelope = {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
    details?: unknown;
  } & Record<string, unknown>;
};

/**
 * Code to assume when a response carries no `ErrorEnvelope`.
 *
 * Not every failing status in the contract declares a body. `runValidation`'s
 * 409 declares none at all, and a network-level failure has none by
 * definition. Defaulting every one of those to `internal_error` reported a
 * stale draft pin to the clinician as an internal server fault — a
 * misclassification the UI then acted on, since #7 must treat a conflict and a
 * crash as different states. These defaults are derived from the status line,
 * which is the one thing every response does have.
 */
const CODE_BY_STATUS: Record<number, string> = {
  401: "unauthenticated",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  422: "validation_failed",
  502: "upstream_unavailable",
  503: "upstream_unavailable",
  504: "upstream_unavailable",
  501: "not_implemented",
};

/**
 * Normalises an openapi-fetch result into either data or a thrown ApiError, so
 * every caller and every TanStack Query hook handles failure the same way.
 */
export function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.data !== undefined) {
    return result.data;
  }

  throw toApiError(result);
}

/**
 * The same normalisation for a response the contract declares as `204`.
 *
 * `unwrap` cannot serve these: openapi-fetch returns `{ data: undefined }` for
 * a 204, which is indistinguishable from a failure by that function's only
 * test. Sending `deleteGithubConnection` through it reported every SUCCESSFUL
 * disconnect as an internal error. Success is decided here on `response.ok`,
 * which is the one signal a body-less response does carry.
 */
export function unwrapNoContent(result: {
  error?: unknown;
  response: Response;
}): void {
  if (result.response.ok) {
    return;
  }
  throw toApiError(result);
}

function toApiError(result: { error?: unknown; response: Response }): ApiError {
  const envelope = result.error as ErrorEnvelope | undefined;
  const status = result.response.status;
  return new ApiError(
    status,
    envelope?.error?.code ?? CODE_BY_STATUS[status] ?? "internal_error",
    envelope?.error?.message ?? `Request failed with HTTP ${status}.`,
    envelope?.error?.requestId,
    envelope?.error?.details,
    envelope?.error,
  );
}
